import { create } from "zustand";
import { persist } from "zustand/middleware";

import { createRawPersistStorage } from "@/lib/platform/clientStorage";

const STORAGE_KEY = "trovie.browse-intro-dismissed";

type BrowseIntroStore = {
  dismissed: boolean;
  loaded: boolean;
  dismiss: () => void;
  finishHydration: () => void;
};
type PersistedBrowseIntro = Pick<BrowseIntroStore, "dismissed">;

const useBrowseIntroStore = create<BrowseIntroStore>()(
  persist<BrowseIntroStore, [], [], PersistedBrowseIntro>(
    (set) => ({
      dismissed: false,
      loaded: false,
      dismiss: () => set({ dismissed: true }),
      finishHydration: () => set({ loaded: true }),
    }),
    {
      name: STORAGE_KEY,
      storage: createRawPersistStorage(
        (value) => ({ dismissed: value === "1" }),
        (state) => state.dismissed ? "1" : "0",
      ),
      partialize: ({ dismissed }) => ({ dismissed }),
      onRehydrateStorage: () => (state) => state?.finishHydration(),
    },
  ),
);

/**
 * Whether the customer has dismissed the branded browse intro (#466), persisted so a returning
 * customer is not greeted again on every scan. Same read-on-mount, write-on-set shape as
 * `usePublicBrowseView`, with its own key because it is a separate, unauthenticated concern.
 * Returns `[dismissed, loaded, dismiss]`: `loaded` is false until the async read resolves, so the
 * screen can hold the intro back for that tick rather than flash it at a customer who already
 * dismissed it.
 */
export function useBrowseIntroDismissed(): readonly [boolean, boolean, () => void] {
  return [
    useBrowseIntroStore((state) => state.dismissed),
    useBrowseIntroStore((state) => state.loaded),
    useBrowseIntroStore((state) => state.dismiss),
  ] as const;
}
